import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  Collapse,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import AlarmOutlinedIcon from "@mui/icons-material/AlarmOutlined";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { colorsFoundation } from "@accredo/fe-shared/theme/index.js";
import ClaimStatusBadge from "../../../components/ClaimStatusBadge.jsx";
import {
  EFFECT_ICONS,
  EFFECT_TYPE_LABELS,
  SEND_TO_LABELS,
  PHASES,
} from "./workflowConstants.js";

function EffectList({ effects }) {
  if (effects.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Keine weiteren Aktionen, nur der Statuswechsel wird protokolliert.
      </Typography>
    );
  }

  return (
    <Stack spacing={1.5}>
      {effects.map((effect, i) => {
        const Icon = EFFECT_ICONS[effect.type];
        return (
          <Box key={i}>
            <Chip
              size="small"
              icon={Icon && <Icon />}
              label={EFFECT_TYPE_LABELS[effect.type] ?? effect.type}
              sx={{ mb: 0.5 }}
            />
            <Typography variant="body2">{effect.title}</Typography>
            <Typography variant="caption" color="text.secondary">
              Empfänger: {effect.recipients}
            </Typography>
            {effect.body && (
              <Box
                sx={{
                  mt: 0.5,
                  p: 1,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Betreff
                </Typography>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  {effect.subject}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: "pre-line" }}
                  data-testid="effect-body"
                >
                  {effect.body}
                </Typography>
              </Box>
            )}
          </Box>
        );
      })}
    </Stack>
  );
}

// Statusliste: Maße der Verbindungslinie und Reihenfolge der Karten.
const LINK_GUTTER = 28;
const LINK_OFFSET = 18;
const CARD_HEADER_CENTER = 24;
const LIST_ORDER = PHASES.flatMap((phase) => phase.states);

// Pfeil zeigt dorthin, wo der andere Status in der Liste steht.
function DirectionIcon({ own_value, other_value, ...props }) {
  return LIST_ORDER.indexOf(other_value) > LIST_ORDER.indexOf(own_value) ? (
    <ArrowDownwardIcon {...props} />
  ) : (
    <ArrowUpwardIcon {...props} />
  );
}

const hasEffect = (transition, effect_type) =>
  transition.effects.some((effect) => effect.type === effect_type);

// Rollen-Badge je Wechsel: haben alle Rollen das Recht, genügt "Alle Rollen".
function RoleBadges({ roles, all_roles_count }) {
  const all = roles.length === all_roles_count;
  const badges = all ? [{ value: "all", label: "Alle Rollen" }] : roles;

  return badges.map((role) => (
    <Chip
      key={role.value}
      variant="outlined"
      size="small"
      icon={<PersonOutlineIcon />}
      label={role.label}
      title={all ? roles.map((r) => r.label).join(", ") : undefined}
    />
  ));
}

function TransitionRow({
  transition,
  label_of,
  link_props,
  effect_filter,
  all_roles_count,
}) {
  const matches_filter = effect_filter && hasEffect(transition, effect_filter);
  // Passende Wechsel starten aufgeklappt (key enthält den Filter, siehe StatusCard).
  const [open, setOpen] = useState(Boolean(matches_filter));
  const has_effects = transition.effects.length > 0;
  const target_label = label_of(transition.to);

  return (
    <Box
      sx={{
        opacity: effect_filter && !matches_filter ? 0.35 : 1,
        transition: "opacity 150ms",
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        gap={0.5}
        onMouseEnter={(event) =>
          link_props.onShowLink(event.currentTarget, transition.to, "out")
        }
        onMouseLeave={link_props.onHideLink}
      >
        <IconButton
          size="small"
          onClick={() => link_props.onGoTo(transition.to)}
          title={`Zu "${target_label}" springen`}
          aria-label={`Zu "${target_label}" springen`}
        >
          <DirectionIcon
            own_value={transition.from}
            other_value={transition.to}
            fontSize="small"
          />
        </IconButton>
        <ButtonBase
          onClick={() =>
            has_effects
              ? setOpen((value) => !value)
              : link_props.onGoTo(transition.to)
          }
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: 1,
            px: 1,
            py: 0.5,
            borderRadius: 1,
            textAlign: "left",
            "&:hover": { bgcolor: "action.hover" },
          }}
        >
          <Typography
            variant="body2"
            color={has_effects ? "text.primary" : "text.secondary"}
            fontWeight={has_effects ? 600 : 400}
          >
            {target_label}
          </Typography>
          {transition.effects.map((effect, i) => {
            const Icon = EFFECT_ICONS[effect.type];
            return (
              Icon && (
                <Icon
                  key={i}
                  sx={{
                    fontSize: 18,
                    color: colorsFoundation.status.critical[700],
                    opacity:
                      effect_filter && effect.type !== effect_filter ? 0.35 : 1,
                  }}
                  titleAccess={EFFECT_TYPE_LABELS[effect.type]}
                />
              )
            );
          })}
          <RoleBadges
            roles={transition.roles}
            all_roles_count={all_roles_count}
          />
          {has_effects &&
            (open ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            ))}
        </ButtonBase>
      </Box>
      <Collapse in={open}>
        <Box pl={6} py={1}>
          <EffectList effects={transition.effects} />
        </Box>
      </Collapse>
    </Box>
  );
}

function StatusCard({
  state,
  transitions,
  label_of,
  highlighted,
  card_ref,
  link_props,
  effect_filter,
  all_roles_count,
  onEditReminder,
  onCreateReminder,
}) {
  const [menu_anchor, setMenuAnchor] = useState(null);
  const outgoing = transitions.filter((t) => t.from === state.value);
  const incoming = transitions.filter((t) => t.to === state.value);
  const faded =
    effect_filter && !outgoing.some((t) => hasEffect(t, effect_filter));

  return (
    <Paper
      ref={card_ref}
      variant="outlined"
      sx={{
        p: 2,
        borderColor: highlighted ? "primary.main" : undefined,
        boxShadow: highlighted
          ? (theme) => `0 0 0 1px ${theme.palette.primary.main}`
          : undefined,
        opacity: faded ? 0.45 : 1,
        transition: "border-color 150ms, box-shadow 150ms, opacity 150ms",
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        gap={1}
      >
        <ClaimStatusBadge
          status={state.value}
          label={state.label}
          size="medium"
        />
        <Box display="flex" alignItems="flex-start" gap={0.5}>
          <Stack spacing={0.5} alignItems="flex-end">
            {state.reminders.map((reminder, i) => (
              <Chip
                key={i}
                size="small"
                variant="outlined"
                sx={{
                  color: colorsFoundation.status.critical[800],
                  borderColor: colorsFoundation.status.critical[700],
                  bgcolor: reminder.active
                    ? colorsFoundation.status.critical[100]
                    : "transparent",
                  "& .MuiChip-icon": {
                    color: colorsFoundation.status.critical[700],
                  },
                }}
                icon={<AlarmOutlinedIcon />}
                onClick={() => onEditReminder(reminder.id)}
                title="Erinnerung bearbeiten"
                label={`${reminder.title} · nach ${
                  reminder.days_until_reminder
                } Tagen an ${
                  SEND_TO_LABELS[reminder.send_to] ?? reminder.send_to
                }${reminder.active ? "" : " (inaktiv)"}`}
              />
            ))}
          </Stack>
          <IconButton
            size="small"
            aria-label="Aktionen"
            onClick={(event) => setMenuAnchor(event.currentTarget)}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={menu_anchor}
            open={Boolean(menu_anchor)}
            onClose={() => setMenuAnchor(null)}
          >
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                onCreateReminder(state.value);
              }}
            >
              Erinnerung erstellen
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {outgoing.length > 0 && (
        <Stack mt={1} spacing={0.25}>
          {outgoing.map((t) => (
            <TransitionRow
              key={`${t.from}->${t.to}:${effect_filter ?? ""}`}
              transition={t}
              label_of={label_of}
              link_props={link_props}
              effect_filter={effect_filter}
              all_roles_count={all_roles_count}
            />
          ))}
        </Stack>
      )}

      {incoming.length > 0 && (
        <Box
          mt={1}
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          gap={0.5}
        >
          <Typography variant="caption" color="text.secondary">
            Erreicht von:
          </Typography>
          {incoming.map((t) => (
            <ButtonBase
              key={`${t.from}->${t.to}`}
              onMouseEnter={(event) =>
                link_props.onShowLink(event.currentTarget, t.from, "in")
              }
              onMouseLeave={link_props.onHideLink}
              onClick={() => link_props.onGoTo(t.from)}
              title={`Zu "${label_of(t.from)}" springen`}
              sx={{
                px: 0.75,
                py: 0.25,
                borderRadius: 1,
                typography: "caption",
                color: "text.secondary",
                "&:hover": { bgcolor: "action.hover", color: "text.primary" },
              }}
            >
              <DirectionIcon
                own_value={t.to}
                other_value={t.from}
                sx={{ fontSize: 14, mr: 0.5 }}
              />
              {label_of(t.from)}
              {t.effects.length > 0 ? " ⚙" : ""}
            </ButtonBase>
          ))}
        </Box>
      )}
    </Paper>
  );
}

export default function StatusList({
  states,
  transitions,
  label_of,
  onEditReminder,
  onCreateReminder,
}) {
  const theme = useTheme();
  const container_ref = useRef(null);
  const card_refs = useRef({});
  const [link, setLink] = useState(null);
  const [flashed, setFlashed] = useState(null);
  const [effect_filter, setEffectFilter] = useState(null);
  const [legend_open, setLegendOpen] = useState(false);

  const all_roles_count = new Set(
    transitions.flatMap((t) => t.roles.map((role) => role.value))
  ).size;
  const effect_types = Object.keys(EFFECT_TYPE_LABELS).filter((type) =>
    transitions.some((t) => hasEffect(t, type))
  );
  const filter_count = effect_filter
    ? transitions.filter((t) => hasEffect(t, effect_filter)).length
    : 0;

  useEffect(() => {
    if (!flashed) return undefined;
    const timeout = setTimeout(() => setFlashed(null), 1500);
    return () => clearTimeout(timeout);
  }, [flashed]);

  // Klammer-Linie im linken Rand: vom überfahrenen Eintrag zur Karte des
  // Zielstatus ("out") bzw. von der Karte des Ausgangsstatus zum Eintrag ("in").
  // Neu vermessen, sobald sich die Liste in der Höhe ändert (z. B. während ein
  // Wechsel auf- oder zuklappt), damit die Linie mitläuft.
  const hover_ref = useRef(null);

  const measureLink = useCallback(() => {
    const hover = hover_ref.current;
    if (!hover) {
      setLink(null);
      return;
    }
    const container = container_ref.current.getBoundingClientRect();
    const card = card_refs.current[hover.value].getBoundingClientRect();
    const own = hover.element.getBoundingClientRect();
    const y_own = own.top + own.height / 2 - container.top;
    const y_card = card.top + CARD_HEADER_CENTER - container.top;
    setLink({
      value: hover.value,
      x: card.left - container.left,
      y_start: hover.direction === "out" ? y_own : y_card,
      y_end: hover.direction === "out" ? y_card : y_own,
    });
  }, []);

  useEffect(() => {
    const observer = new ResizeObserver(measureLink);
    observer.observe(container_ref.current);
    return () => observer.disconnect();
  }, [measureLink]);

  const hideLink = () => {
    hover_ref.current = null;
    setLink(null);
  };

  const link_props = {
    onShowLink: (element, value, direction) => {
      hover_ref.current = { element, value, direction };
      measureLink();
    },
    onHideLink: hideLink,
    onGoTo: (value) => {
      hideLink();
      card_refs.current[value].scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      setFlashed(value);
    },
  };

  const link_color = theme.palette.primary.main;

  return (
    <>
      <Box display="flex" justifyContent="flex-end">
        <Button
          size="small"
          color="inherit"
          onClick={() => setLegendOpen((open) => !open)}
          endIcon={legend_open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        >
          Legende
        </Button>
      </Box>
      <Collapse in={legend_open}>
        <Box
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          columnGap={3}
          rowGap={1}
          py={1}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Chip
              size="small"
              variant="outlined"
              icon={<PersonOutlineIcon />}
              label="Rolle"
            />
            <Typography variant="body2" color="text.secondary">
              Rollen, die den Wechsel auslösen dürfen (&quot;Alle Rollen&quot;:
              jede Rolle mit Schreibrecht)
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Chip
              size="small"
              variant="outlined"
              icon={<AlarmOutlinedIcon />}
              label="Erinnerung"
              sx={{
                color: colorsFoundation.status.critical[800],
                borderColor: colorsFoundation.status.critical[700],
                bgcolor: colorsFoundation.status.critical[100],
                "& .MuiChip-icon": {
                  color: colorsFoundation.status.critical[700],
                },
              }}
            />
            <Typography variant="body2" color="text.secondary">
              Erinnerung am Status, Klick zum Bearbeiten (ohne Füllung: inaktiv)
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <MoreVertIcon fontSize="small" />
            <Typography variant="body2" color="text.secondary">
              Weitere Erinnerung für den Status erstellen
            </Typography>
          </Box>
        </Box>
      </Collapse>
      <Box
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        gap={1}
        mb={2}
        pl={`${LINK_GUTTER}px`}
      >
        <Typography variant="body2" color="text.secondary">
          Filter nach Aktion:
        </Typography>
        {effect_types.map((type) => {
          const Icon = EFFECT_ICONS[type];
          const selected = effect_filter === type;
          return (
            <Chip
              key={type}
              size="small"
              icon={Icon && <Icon />}
              label={EFFECT_TYPE_LABELS[type]}
              color={selected ? "primary" : "default"}
              variant={selected ? "filled" : "outlined"}
              onClick={() => setEffectFilter(selected ? null : type)}
              onDelete={selected ? () => setEffectFilter(null) : undefined}
            />
          );
        })}
        {effect_filter && (
          <Typography variant="body2" color="text.secondary">
            {filter_count} Wechsel {filter_count === 1 ? "löst" : "lösen"} diese
            Aktion aus
          </Typography>
        )}
      </Box>

      <Box ref={container_ref} position="relative" pl={`${LINK_GUTTER}px`}>
        {link && (
          <svg
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              pointerEvents: "none",
              overflow: "visible",
            }}
          >
            <defs>
              <marker
                id="workflow-link-arrow"
                viewBox="0 0 10 10"
                refX="9"
                refY="5"
                markerWidth="6"
                markerHeight="6"
                orient="auto"
              >
                <path d="M0,0 L10,5 L0,10 z" fill={link_color} />
              </marker>
            </defs>
            <path
              d={`M ${link.x} ${link.y_start} H ${link.x - LINK_OFFSET} V ${
                link.y_end
              } H ${link.x}`}
              fill="none"
              stroke={link_color}
              strokeWidth={2}
              markerEnd="url(#workflow-link-arrow)"
            />
          </svg>
        )}

        <Stack spacing={3}>
          {PHASES.map((phase) => (
            <Box key={phase.id}>
              <Typography variant="overline" color="text.secondary">
                {phase.label}
              </Typography>
              <Stack spacing={1.5}>
                {phase.states
                  .map((value) => states.find((state) => state.value === value))
                  .filter(Boolean)
                  .map((state) => (
                    <StatusCard
                      key={state.value}
                      state={state}
                      transitions={transitions}
                      label_of={label_of}
                      all_roles_count={all_roles_count}
                      onEditReminder={onEditReminder}
                      onCreateReminder={onCreateReminder}
                      highlighted={
                        link?.value === state.value || flashed === state.value
                      }
                      card_ref={(element) => {
                        card_refs.current[state.value] = element;
                      }}
                      link_props={link_props}
                      effect_filter={effect_filter}
                    />
                  ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      </Box>
    </>
  );
}
